<template>
  <div class="summary-page">
    <div class="fixed-background">
      <div class="ambient-light main-spot" :style="{ background: splitColor }"></div>
      <div class="ambient-light secondary-spot" :style="{ background: splitColor }"></div>
      <div class="noise-texture"></div>
    </div>

    <div class="glass-header">
      <v-btn icon large color="white" @click="$router.go(-1)" class="back-btn">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <div class="header-content">
        <span class="header-title">ZUSAMMENFASSUNG</span>
        <span class="header-date">{{ currentDate }}</span>
      </div>
      <div style="width: 44px"></div>
    </div>

    <div class="scroll-content">
      <div v-if="isHyroxSummary" class="hyrox-container pt-8">
        <div class="text-center mb-8">
          <h1 class="hyrox-title" :style="{ color: splitColor }">RACE FINISHED</h1>
          <div class="hyrox-time">{{ durationString }}</div>
          <p class="white--text opacity-70">{{ activeSplit.title }}</p>
        </div>

        <v-list dark color="transparent" class="pa-0">
          <template v-for="(segment, index) in hyroxSegments">
            <v-list-item :key="index" class="glass-list-item mb-2 mx-4" @click="toggleSegment(index)">
              <v-list-item-avatar tile size="44" class="rounded-lg">
                <v-img :src="`/img/uebungen/${segment.icon.replace(/\.[^/.]+$/, '')}.webp`"></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold white--text">{{ segment.title }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="text-right">
                <div class="font-weight-bold white--text text-h6">{{ segment.time }}</div>
                <div v-if="segment.comparison" class="caption font-weight-bold" :class="segment.comparisonClass">
                  {{ segment.comparison }}
                </div>
              </v-list-item-action>
              <v-icon small color="grey lighten-1" :class="{ 'rotate-180': expandedSegmentIndex === index }">mdi-chevron-down</v-icon>
            </v-list-item>

            <v-expand-transition :key="`expand-${index}`">
              <div v-if="expandedSegmentIndex === index" class="mx-4 mb-4 px-4 py-2 history-bg rounded-lg">
                <div class="caption grey--text mb-2">Verlauf:</div>
                <div v-for="(item, i) in getSegmentHistory(segment)" :key="i" class="d-flex justify-space-between text-body-2 mb-1">
                  <span class="white--text">{{ item.date }}</span>
                  <span class="font-weight-bold" :style="{ color: splitColor }">{{ item.time }}</span>
                </div>
              </div>
            </v-expand-transition>
          </template>
        </v-list>
      </div>

      <template v-else>
        <v-container class="pt-6 pb-16">
          <div class="text-center mb-8">
            <div class="hero-avatar mb-4" :style="{ boxShadow: `0 0 40px ${splitColor}55`, borderColor: splitColor }">
              <v-img :src="splitImage" height="100%" width="100%">
                <template v-slot:placeholder>
                  <v-sheet color="grey darken-3" class="fill-height d-flex align-center justify-center">
                    <v-icon x-large color="grey">mdi-dumbbell</v-icon>
                  </v-sheet>
                </template>
              </v-img>
            </div>

            <h2 class="text-h4 font-weight-black white--text text-uppercase mb-2 shadow-text">
              {{ splitTitle }}
            </h2>

            <div class="d-flex justify-center flex-wrap" style="gap: 8px">
              <v-chip
                v-for="tag in focusTags"
                :key="tag"
                outlined
                :color="splitColor"
                class="font-weight-bold px-4"
                style="border-width: 2px; background: rgba(0, 0, 0, 0.4)">
                {{ tag }}
              </v-chip>
            </div>
          </div>

          <div v-if="newPersonalRecords.length > 0" class="mb-8">
            <div class="section-title mb-3 pl-1">
              <v-icon color="#FFD700" size="20" class="mr-2">mdi-trophy</v-icon>
              NEUE REKORDE
            </div>
            <v-slide-group show-arrows="desktop" class="pr-slider">
              <v-slide-item v-for="(pr, i) in newPersonalRecords" :key="i">
                <div class="glass-card pr-card mr-3 pa-4">
                  <div class="pr-glow"></div>
                  <div class="font-weight-bold white--text text-truncate mb-1">{{ pr.exerciseTitle }}</div>
                  <div class="caption grey--text mb-3">{{ pr.type }}</div>
                  <div class="d-flex align-center justify-space-between">
                    <span class="grey--text text-decoration-line-through caption">{{ pr.oldValue }}</span>
                    <v-icon x-small color="white">mdi-arrow-right</v-icon>
                    <span class="font-weight-black text-h6" :style="{ color: '#00E676' }">{{ pr.newValue }}</span>
                  </div>
                </div>
              </v-slide-item>
            </v-slide-group>
          </div>

          <div class="stats-grid mb-8">
            <div
              class="glass-card stat-hero pa-5 relative overflow-hidden d-flex align-center justify-space-between ripple-effect"
              :style="{ borderTop: `4px solid ${splitColor}` }"
              @click="nextFunFact">
              <div class="glow-bg" :style="{ background: `radial-gradient(circle, ${currentFunFact.color}33 0%, transparent 70%)` }"></div>

              <div style="position: relative; z-index: 2; flex-grow: 1">
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="caption font-weight-bold grey--text text--lighten-1 text-uppercase">Bewegte Last</div>

                  <div class="tap-hint-badge">
                    <v-icon x-small color="white" class="mr-1" style="opacity: 0.9">mdi-cursor-default-click</v-icon>
                    TIPPEN
                  </div>
                </div>

                <v-fade-transition mode="out-in">
                  <div :key="currentFunFact.label" class="d-flex align-baseline flex-wrap mt-1">
                    <span class="text-h3 font-weight-black white--text mr-2" style="line-height: 1">
                      {{ currentFunFact.count }}
                    </span>
                    <span class="text-h6 font-weight-bold text-truncate" :style="{ color: currentFunFact.color }">
                      {{ currentFunFact.label }}
                    </span>
                  </div>
                </v-fade-transition>

                <div class="caption grey--text mt-2 d-flex align-center">
                  <v-icon x-small color="grey" class="mr-1">mdi-weight-kilogram</v-icon>
                  <span>{{ animatedVolume.toLocaleString("de-DE") }} kg total</span>
                </div>
              </div>

              <v-fade-transition mode="out-in">
                <div
                  :key="currentFunFact.icon"
                  class="fun-fact-icon-wrapper ml-3"
                  :style="{ borderColor: currentFunFact.color, boxShadow: `0 0 20px ${currentFunFact.color}20` }">
                  <v-icon size="40" :color="currentFunFact.color" class="fun-fact-icon">
                    {{ currentFunFact.icon }}
                  </v-icon>
                </div>
              </v-fade-transition>
            </div>
            <div class="glass-card pa-4 d-flex flex-column justify-center text-center relative ripple-effect" @click="openDurationDialog">
              <div class="edit-circle" :style="{ background: splitColor }">
                <v-icon x-small color="black">mdi-pencil</v-icon>
              </div>
              <div class="caption grey--text text-uppercase mb-1">Dauer</div>
              <div class="text-h5 font-weight-bold white--text">{{ durationString }}</div>
            </div>

            <div class="glass-card pa-4 relative ripple-effect" @click="showCaloriesDialog = true">
              <div class="edit-circle" :style="{ background: splitColor }">
                <v-icon x-small color="black">mdi-pencil</v-icon>
              </div>
              <div class="caption grey--text text-uppercase mb-1">Kalorien</div>
              <div class="d-flex align-baseline justify-center">
                <span class="text-h5 font-weight-bold white--text mr-1">{{ displayCalories }}</span>
                <span class="caption grey--text">kcal</span>
              </div>
            </div>

            <div class="glass-card pa-4 text-center">
              <div class="text-h4 font-weight-bold white--text mb-1">{{ animatedSets }}</div>
              <div class="caption grey--text text-uppercase">Sätze</div>
            </div>

            <div class="glass-card pa-4 text-center">
              <div class="text-h4 font-weight-bold white--text mb-1">{{ animatedReps }}</div>
              <div class="caption grey--text text-uppercase">Reps</div>
            </div>

            <div v-if="animatedDistance > 0" class="glass-card full-width pa-4 d-flex align-center justify-space-between">
              <div>
                <div class="caption grey--text text-uppercase">Distanz</div>
                <span class="text-h5 font-weight-bold white--text">{{ animatedDistance }} m</span>
              </div>
              <v-icon color="white" style="opacity: 0.3" large>mdi-map-marker-distance</v-icon>
            </div>
          </div>

          <div class="mb-12">
            <div class="section-title mb-2 pl-1">Hilf uns, besser zu werden!</div>
            <v-textarea
              v-model="trainingFeedback"
              class="bright-input"
              placeholder="Was können wir an der App verbessern? Dein Feedback geht direkt an uns Entwickler (optional)."
              rows="3"
              auto-grow
              solo
              flat
              hide-details></v-textarea>
          </div>
        </v-container>
      </template>
    </div>

    <v-footer fixed class="transparent-footer pa-4">
      <div class="footer-blur"></div>
      <v-btn
        x-large
        block
        rounded
        class="save-btn font-weight-bold"
        :style="{ background: splitColor, color: isLightColor(splitColor) ? 'black' : 'white' }"
        @click="saveSessionAndExit"
        :loading="isSaving"
        elevation="10">
        TRAINING SPEICHERN
        <v-icon right>mdi-content-save-check</v-icon>
      </v-btn>
    </v-footer>

    <v-dialog v-model="showCaloriesDialog" max-width="320" content-class="dialog-card">
      <v-card class="glass-modal pa-5 text-center">
        <h3 class="white--text text-h6 font-weight-bold mb-4">Kalorien anpassen</h3>
        <div class="input-display mb-6">
          <input v-model.number="manualCalories" type="number" class="big-input" placeholder="0" autofocus />
          <span class="unit">kcal</span>
        </div>
        <v-btn block x-large rounded :color="splitColor" class="font-weight-bold black--text" @click="showCaloriesDialog = false"> Übernehmen </v-btn>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDurationDialog" max-width="320" content-class="dialog-card">
      <v-card class="glass-modal pa-5 text-center">
        <h3 class="white--text text-h6 font-weight-bold mb-4">Training anpassen</h3>

        <div class="caption grey--text mb-1">Dauer</div>
        <div class="input-display mb-4">
          <input v-model.number="tempDurationMin" type="number" class="big-input" placeholder="0" autofocus />
          <span class="unit">min</span>
        </div>

        <!--
        <div class="caption grey--text mb-1">Datum</div>
        <div class="input-display mb-6">
          <input v-model="tempDateStr" type="date" class="big-input" style="font-size: 1.2rem; width: 100%; text-align: center" />
        </div>
        -->

        <v-btn block x-large rounded :color="splitColor" class="font-weight-bold black--text" @click="applyDurationEdit"> Übernehmen </v-btn>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showSaveFreeTrainingDialog" max-width="380" content-class="dialog-card">
      <v-card class="glass-modal pa-5">
        <div class="text-center mb-6">
          <v-avatar size="60" :color="splitColor + '33'" class="mb-3">
            <v-icon large :color="splitColor">mdi-content-save-edit</v-icon>
          </v-avatar>
          <div class="white--text font-weight-bold text-h6">Training sichern</div>
        </div>
        <v-card class="action-card mb-3" ripple @click="handleSaveFreeTraining(true)">
          <div class="d-flex align-center pa-3">
            <v-icon :color="splitColor" class="mr-3">mdi-playlist-plus</v-icon>
            <div>
              <div class="white--text font-weight-bold">Als Plan speichern</div>
              <div class="grey--text caption">Für die Zukunft merken</div>
            </div>
          </div>
        </v-card>
        <v-card class="action-card" ripple @click="handleSaveFreeTraining(false)">
          <div class="d-flex align-center pa-3">
            <v-icon color="white" class="mr-3">mdi-check</v-icon>
            <div>
              <div class="white--text font-weight-bold">Nur Session</div>
              <div class="grey--text caption">Einmaliges Workout</div>
            </div>
          </div>
        </v-card>
      </v-card>
    </v-dialog>

    <save-changes-dialog
      :dialog.sync="saveChangesDialogVisible"
      :modifications="getWorkoutModifications"
      @save-changes="handleSaveChanges"
      @discard-changes="handleDiscardChanges" />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"
import api from "@/api"
import moment from "moment"
import confetti from "canvas-confetti"
import SaveChangesDialog from "./saveChangesDialog.vue"

export default {
  name: "WorkoutSummary",
  components: { SaveChangesDialog },
  data() {
    return {
      funFactIndex: 0,
      expandedSegmentIndex: null,
      trainingFeedback: "",
      isSaving: false,
      saveChangesDialogVisible: false,
      showSaveFreeTrainingDialog: false,
      showCaloriesDialog: false,

      // Animation & Werte
      animatedVolume: 0,
      animatedSets: 0,
      animatedReps: 0,
      animatedDistance: 0,
      animatedCalories: 0,
      manualCalories: null,

      unixTimestamp: null,

      showDurationDialog: false,

      // Manuelle Overrides (null = Standard-Berechnung)
      manualDurationSeconds: null,
      manualTimestamp: null,

      // Temp Werte für das Dialog-Feld
      tempDurationMin: 0,
      tempDateStr: "",
    }
  },
  computed: {
    ...mapState("runWorkoutStore", [
      "activeSplit",
      "originalActiveSplit",
      "completedSetsMap",
      "workoutStartTime",
      "hubGroupedExercises",
      "activePlanID",
      "hubGroupedSessions",
      "exerciseNotesMap",
    ]),
    ...mapGetters("runWorkoutStore", ["getWorkoutModifications"]),
    currentFunFact() {
      const vol = this.animatedVolume || 0
      const fact = this.funFactScale[this.funFactIndex]

      // Berechnung
      let countVal = (vol / fact.weight).toFixed(1)
      if (countVal.endsWith(".0")) countVal = countVal.replace(".0", "")

      // Tausender-Trennzeichen für riesige Hamster-Zahlen :)
      countVal = Number(countVal).toLocaleString("de-DE")

      return {
        ...fact,
        count: countVal,
      }
    },
    funFactScale() {
      return [
        { weight: 0.15, label: "Mäuse", singular: "Maus", icon: "mdi-rodent", color: "#BCAAA4" },
        { weight: 0.5, label: "Protein Drinks", singular: "Protein Drink", icon: "mdi-beer", color: "#FFD54F" },
        { weight: 4, label: "Hauskatzen", singular: "Katze", icon: "mdi-cat", color: "#FFB74D" },
        { weight: 70, label: "Waschmaschinen", singular: "Waschmaschine", icon: "mdi-washing-machine", color: "#90CAF9" },
        { weight: 180, label: "Löwen", singular: "Löwe", icon: "mdi-paw", color: "#FF8A65" },
        { weight: 700, label: "Kühe", singular: "Kuh", icon: "mdi-cow", color: "#A5D6A7" },
        { weight: 1200, label: "Kleinwagen", singular: "VW Golf", icon: "mdi-car-hatchback", color: "#9FA8DA" },
        { weight: 5000, label: "Elefanten", singular: "Elefant", icon: "mdi-elephant", color: "#CE93D8" },
        { weight: 180000, label: "Blauwale", singular: "Blauwal", icon: "mdi-fish", color: "#4FC3F7" },
      ]
    },
    // --- DESIGN ---
    splitColor() {
      return this.activeSplit?.fixedVisualization?.color || "#00E5FF"
    },
    splitTitle() {
      return this.activeSplit?.title || "Freies Training"
    },
    splitImage() {
      return this.activeSplit?.fixedVisualization?.img || ""
    },
    currentDate() {
      if (this.manualTimestamp) {
        return moment.unix(this.manualTimestamp).format("D. MMM YYYY")
      }
      return moment().format("D. MMM YYYY")
    },

    // UPDATE: Dauer berücksichtigt manuelle Eingabe
    durationInSeconds() {
      // Wenn manuell gesetzt, nimm diesen Wert
      if (this.manualDurationSeconds !== null) return this.manualDurationSeconds

      if (!this.workoutStartTime) return 0
      return moment().diff(moment(this.workoutStartTime), "seconds")
    },

    // --- KALORIEN ---
    displayCalories() {
      return this.manualCalories !== null ? this.manualCalories : this.animatedCalories.toFixed(0)
    },
    calculatedCalories() {
      return (this.durationInSeconds / 60) * 7
    }, // ~7 kcal/min

    // --- ZEIT ---

    durationString() {
      const s = this.durationInSeconds
      const h = Math.floor(s / 3600),
        m = Math.floor((s % 3600) / 60)
      const p = (n) => String(n).padStart(2, "0")
      return h > 0 ? `${p(h)}:${p(m)}:${p(s % 60)}` : `${p(m)}:${p(s % 60)}`
    },

    // --- STATS AGGREGATION ---
    totalVolume() {
      return Object.values(this.completedSetsMap)
        .flat()
        .reduce((acc, s) => {
          if (s.skipped) return acc
          const w = Number(s.find((i) => i.text === "Gewicht")?.value || 0)
          const r = Number(s.find((i) => /Wiederholungen/.test(i.text))?.value || 0)
          return acc + w * r
        }, 0)
    },
    totalSets() {
      return Object.values(this.completedSetsMap)
        .flat()
        .filter((s) => !s.skipped).length
    },
    totalReps() {
      return Object.values(this.completedSetsMap)
        .flat()
        .reduce((acc, s) => {
          if (s.skipped) return acc
          return acc + Number(s.find((i) => /Wiederholungen/.test(i.text))?.value || 0)
        }, 0)
    },
    totalDistance() {
      return Object.values(this.completedSetsMap)
        .flat()
        .reduce((acc, s) => {
          if (s.skipped) return acc
          return acc + Number(s.find((i) => i.text === "Distanz")?.value || 0)
        }, 0)
    },

    // --- TAGS ---
    focusTags() {
      if (!this.activeSplit?.exercises) return []
      const allTags = this.activeSplit.exercises.flatMap((ex) => {
        if (ex.isSuperset && Array.isArray(ex.exercises)) {
          return ex.exercises.flatMap((subEx) => subEx.tags?.map((tag) => tag.name) || [])
        }
        return ex.tags?.map((tag) => tag.name) || []
      })
      return [...new Set(allTags)].slice(0, 3)
    },

    // --- PERSONAL RECORDS (Vollständige Logik) ---
    newPersonalRecords() {
      if (!this.hubGroupedExercises || !this.completedSetsMap || !this.activeSplit) return []
      const records = []
      Object.entries(this.completedSetsMap).forEach(([exerciseIndex, sets]) => {
        if (!sets || sets.length === 0) return
        const exerciseDef = this.activeSplit.exercises[exerciseIndex]
        if (!exerciseDef) return
        const processExercise = (subExercise, subSets) => {
          const historicalData = this.hubGroupedExercises.find(
            (ex) => ex.exercise === subExercise._id || (ex.exercise && ex.exercise._id === subExercise._id),
          )
          if (!historicalData) return
          let currentMaxWeight = 0
          subSets.forEach((set) => {
            if (set.skipped) return
            const weight = Number(set.find((item) => item.text === "Gewicht")?.value || 0)
            if (weight > currentMaxWeight) currentMaxWeight = weight
          })
          const oldMaxWeight = historicalData.summary?.maxWeightStep?.weight || 0
          if (currentMaxWeight > oldMaxWeight) {
            records.push({
              exerciseTitle: subExercise.title,
              type: "Max Weight",
              oldValue: `${oldMaxWeight} kg`,
              newValue: `${currentMaxWeight} kg`,
            })
          }
        }
        if (exerciseDef.isSuperset) {
          const setsBySubExercise = sets.reduce((acc, set) => {
            if (set.subExerciseId) {
              if (!acc[set.subExerciseId]) acc[set.subExerciseId] = []
              acc[set.subExerciseId].push(set)
            }
            return acc
          }, {})
          Object.entries(setsBySubExercise).forEach(([subExerciseId, subSets]) => {
            const subExerciseDef = exerciseDef.exercises.find((ex) => ex._id === subExerciseId)
            if (subExerciseDef) processExercise(subExerciseDef, subSets)
          })
        } else {
          processExercise(exerciseDef, sets)
        }
      })
      return records
    },

    // --- HYROX LOGIK (Vollständig) ---
    isHyroxSummary() {
      return this.activeSplit?.isHyrox
    },
    previousHyroxRace() {
      if (!this.activeSplit || !this.hubGroupedSessions) return null
      const pastRaces = this.hubGroupedSessions.filter(
        (session) => session.execution && session.execution.title === this.activeSplit.title && session.execution.hyroxSegments,
      )
      return pastRaces.length > 0 ? pastRaces[0] : null
    },
    hyroxSegments() {
      if (!this.activeSplit?.isHyrox || !this.completedSetsMap) return []
      const previousRaceSegments = this.previousHyroxRace?.execution?.hyroxSegments || []
      return this.activeSplit.exercises
        .map((exercise, index) => {
          const completedSet = (this.completedSetsMap[index] || [])[0]
          if (!completedSet) return null
          const durationInSeconds = Number(completedSet.find((s) => s.text === "Dauer")?.value || 0)

          const formatTime = (seconds) => {
            if (isNaN(seconds) || seconds <= 0) return "---"
            const min = Math.floor(seconds / 60)
            const sec = Math.floor(seconds % 60)
            return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
          }

          let comparison = null
          let comparisonClass = ""
          const previousSegment = previousRaceSegments.find((seg) => seg.segmentIndex === index)
          if (previousSegment && previousSegment.duration > 0 && durationInSeconds > 0) {
            const diff = durationInSeconds - previousSegment.duration
            const sign = diff <= 0 ? "-" : "+"
            comparisonClass = diff <= 0 ? "success--text" : "error--text"
            comparison = `${sign}${formatTime(Math.abs(diff))}`
          }
          return {
            title: exercise.title,
            exerciseId: exercise._id,
            time: formatTime(durationInSeconds),
            comparison,
            comparisonClass,
            icon: this.getImageUrl(exercise.picture || exercise.manual?.[0]?.picture),
            segmentIndex: index,
          }
        })
        .filter(Boolean)
    },
    hasExistingPlans() {
      return !!this.activePlanID
    },
    didNotesChange() {
      return Object.keys(this.exerciseNotesMap).length > 0
    },
  },
  methods: {
    ...mapActions("runWorkoutStore", ["resetWorkoutState", "stopTimer"]),
    nextFunFact() {
      // Vibrations-Feedback für Haptik
      if (navigator.vibrate) navigator.vibrate(15)

      this.funFactIndex = (this.funFactIndex + 1) % this.funFactScale.length
    },

    // Intelligenter Start: Wähle beim Laden das passendste Tier
    setInitialFunFact() {
      const vol = this.totalVolume || 0
      // Finde den Index, wo das Gewicht am besten passt (Logic wie vorher)
      // Wir suchen das Tier, dessen Gewicht am nächsten an "vol / 2" ist oder einfach logisch passt
      // Einfache Logik: Wir nehmen das grösste Tier, das kleiner als das Gesamtvolumen ist
      let bestIndex = 0

      // Wir loopen durch und schauen, was Sinn macht (z.B. nicht 0.01 Elefanten)
      // Ziel: Mindestens 1.0 von dem Tier
      for (let i = 0; i < this.funFactScale.length; i++) {
        if (vol >= this.funFactScale[i].weight) {
          bestIndex = i
        }
      }
      this.funFactIndex = bestIndex
    },
    formatNumber(val) {
      return new Intl.NumberFormat("de-DE").format(Math.round(val))
    },
    openDurationDialog() {
      // Aktuelle Minuten berechnen
      this.tempDurationMin = Math.round(this.durationInSeconds / 60)
      // Aktuelles Datum setzen (YYYY-MM-DD für den Input)
      this.tempDateStr = this.manualTimestamp ? moment.unix(this.manualTimestamp).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")

      this.showDurationDialog = true
    },

    // NEU: Werte übernehmen
    applyDurationEdit() {
      // 1. Dauer speichern (Minuten -> Sekunden)
      this.manualDurationSeconds = this.tempDurationMin * 60

      // 2. Datum speichern (String -> Unix Timestamp am Ende des Tages)
      // Wir nehmen z.B. 12:00 Uhr mittags des gewählten Tages oder behalten die aktuelle Uhrzeit bei
      if (this.tempDateStr) {
        this.manualTimestamp = moment(this.tempDateStr, "YYYY-MM-DD").hours(12).minutes(0).unix()
      }

      this.showDurationDialog = false

      // Optional: Kalorien neu berechnen, da sich die Zeit geändert hat
      this.animateStats()
    },
    // Helper
    isLightColor(c) {
      const hex = c.replace("#", "")
      const r = parseInt(hex.substr(0, 2), 16),
        g = parseInt(hex.substr(2, 2), 16),
        b = parseInt(hex.substr(4, 2), 16)
      return (r * 299 + g * 587 + b * 114) / 1000 > 155
    },
    getImageUrl(id) {
      if (!id) return "/img/default-exercise.webp"
      return `/img/uebungen/${String(id).replace(/\.[^/.]+$/, "")}.webp`
    },

    // Hyrox
    toggleSegment(index) {
      this.expandedSegmentIndex = this.expandedSegmentIndex === index ? null : index
    },
    getSegmentHistory(segment) {
      if (!this.hubGroupedSessions || !segment || !this.activeSplit) return []
      const history = []
      const pastRaces = this.hubGroupedSessions.filter(
        (session) => session.execution && session.execution.title === this.activeSplit.title && session.execution.hyroxSegments,
      )
      pastRaces.forEach((race) => {
        const pastSegment = race.execution.hyroxSegments.find((s) => s.segmentIndex === segment.segmentIndex)
        if (pastSegment && pastSegment.duration > 0) {
          const duration = Number(pastSegment.duration)
          const minutes = Math.floor(duration / 60)
          const seconds = Math.floor(duration % 60)
          history.push({
            date: moment(parseInt(race.execution.ts, 10) * 1000).format("DD.MM.YY"),
            time: `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`,
          })
        }
      })
      if (segment.time !== "---") history.unshift({ date: "Heute", time: segment.time })
      return history
    },

    // Speichern
    async saveSessionAndExit() {
      if (this.isSaving) return
      if (this.activeSplit.isFreeTraining && !this.activeSplit.isHyrox) {
        if (this.hasExistingPlans) this.showSaveFreeTrainingDialog = true
        else await this.executeSave({ shouldSaveAsSplit: true, planId: null })
      } else {
        await this.executeSave({ shouldSaveAsSplit: false, planId: this.activePlanID })
      }
    },
    async handleSaveFreeTraining(saveAsSplit) {
      this.showSaveFreeTrainingDialog = false
      await this.executeSave({ shouldSaveAsSplit: saveAsSplit, planId: saveAsSplit ? null : this.activePlanID })
    },
    async executeSave({ shouldSaveAsSplit, planId }) {
      try {
        this.stopTimer()
      } catch (e) {
        console.log(e)
      }
      this.isSaving = true
      let finalPlanId = planId
      try {
        if (shouldSaveAsSplit) {
          const splitToSave = { ...this.activeSplit }
          delete splitToSave.isFreeTraining
          const response = await api.workoutApi.addSplit({ workout_id: null, split: splitToSave })
          if (response?._id) finalPlanId = response._id
        }

        this.unixTimestamp = this.manualTimestamp ? this.manualTimestamp : moment().unix()
        const isHyrox = this.activeSplit.isHyrox
        const finalCalories = this.manualCalories !== null ? this.manualCalories : this.calculatedCalories.toFixed(0)

        const executionData = {
          title: this.activeSplit.title,
          fixedVisualization: this.activeSplit.fixedVisualization,
          workout_id: finalPlanId,
          duration: this.durationInSeconds,
          records: this.newPersonalRecords,
          liftedWeight: this.totalVolume,
          totalDistance: this.totalDistance,
          caloriesBurned: finalCalories,
          feedBack: this.trainingFeedback,
          ts: this.unixTimestamp,
        }

        let legacySets = []
        if (isHyrox) {
          executionData.hyroxSegments = this.activeSplit.exercises.map((exercise, index) => {
            const completedSet = (this.completedSetsMap[index] || [])[0]
            const duration = completedSet ? Number(completedSet.find((s) => s.text === "Dauer")?.value || 0) : 0
            const weight = completedSet ? Number(completedSet.find((s) => s.text === "Gewicht")?.value || 0) : 0
            const reps = completedSet ? Number(completedSet.find((s) => /Wiederholungen/.test(s.text))?.value || 0) : 0
            const distance = completedSet ? Number(completedSet.find((s) => s.text === "Distanz")?.value || 0) : 0
            return {
              segmentIndex: index,
              title: this.originalActiveSplit.exercises[index]?.title || exercise.title,
              duration: duration,
              exercise_id: exercise._id,
              weight: weight,
              reps: reps,
              distance: distance,
            }
          })
        } else {
          legacySets = this.transformSetsForBackend(finalPlanId)
        }

        await api.workoutApi.saveWorkoutSession({ execution: executionData, sets: legacySets })

        // Updates checken (für Weights/Notes)
        if (!this.activeSplit.isFreeTraining && (this.didWeightsChange() || this.didNotesChange)) {
          const splitWithUpdatedWeights = this.getUpdatedSplit([])
          await api.workoutApi.updateSplit({ workout_id: finalPlanId, split: splitWithUpdatedWeights })
        }

        this.resetWorkoutState()
        this.$router.push("/workout/hub")
      } catch (error) {
        console.error(error)
        this.$toast.error("Fehler beim Speichern")
      } finally {
        this.isSaving = false
      }
    },
    transformSetsForBackend(freeTrainingWorkoutID = null) {
      const exerciseExecutions = []
      Object.entries(this.completedSetsMap).forEach(([exerciseIndex, performedSets]) => {
        if (!performedSets || performedSets.length === 0 || performedSets.every((s) => s.skipped)) return
        const exercise = this.activeSplit.exercises[exerciseIndex]
        if (!exercise) return
        const steps = performedSets
          .map((set, setIndex) => {
            if (set.skipped) return null
            return {
              set: setIndex + 1,
              weight: Number(set.find((item) => item.text === "Gewicht")?.value || 0),
              reps: Number(set.find((item) => /Wiederholungen/.test(item.text))?.value || 0),
              distance: Number(set.find((item) => item.text === "Distanz")?.value || 0),
              time: set.find((item) => item.text === "Dauer")?.value || null,
              subExerciseId: set.subExerciseId || null, // FIX: SubExerciseId für Supersätze speichern!
              feedback: {
                difficulty: set.find((item) => item.text === "Difficulty")?.value,
                newWeightSuggestion: set.find((item) => item.text === "newWeight")?.value,
              },
            }
          })
          .filter(Boolean)
        if (steps.length > 0) {
          exerciseExecutions.push({
            exercise_id: exercise._id,
            split: this.activeSplit.title,
            workoutId: freeTrainingWorkoutID || this.activePlanID,
            timestamp: this.unixTimestamp,
            steps: steps,
          })
        }
      })
      return exerciseExecutions
    },
    getUpdatedSplit(modificationsToApply) {
      const splitCopy = JSON.parse(JSON.stringify(this.originalActiveSplit))

      // 1. Struktur-Anpassungen (Swap/Add/Delete) anwenden
      if (modificationsToApply && modificationsToApply.length > 0) {
        modificationsToApply.forEach((mod) => {
          if (mod.type === "swap") {
            const targetIndex = splitCopy.exercises.findIndex((ex) => ex._id === mod.payload.originalExercise._id)
            if (targetIndex > -1) splitCopy.exercises[targetIndex] = mod.payload.newExercise
          }
        })
        const deletions = modificationsToApply.filter((mod) => mod.type === "delete")
        deletions.forEach((mod) => {
          const targetIndex = splitCopy.exercises.findIndex((ex) => ex._id === mod.payload.exercise._id)
          if (targetIndex > -1) {
            splitCopy.exercises.splice(targetIndex, 1)
          }
        })
        const additions = modificationsToApply.filter((mod) => mod.type === "add").sort((a, b) => a.payload.atIndex - b.payload.atIndex)
        additions.forEach((mod) => splitCopy.exercises.splice(mod.payload.atIndex, 0, mod.payload.newExercise))
      }

      // 2. Gewichte aktualisieren
      Object.entries(this.completedSetsMap).forEach(([exerciseIndex, performedSets]) => {
        const performedExerciseId = this.activeSplit.exercises[exerciseIndex]._id
        const exerciseToUpdateInCopy = splitCopy.exercises.find((ex) => ex._id === performedExerciseId)

        if (!exerciseToUpdateInCopy) return

        // === FALL A: SUPERSATZ ===
        if (exerciseToUpdateInCopy.isSuperset) {
          const subExerciseSetCounters = {}

          performedSets.forEach((performedSet) => {
            // Nur Sätze mit Sub-ID beachten
            if (!performedSet.subExerciseId) return

            const subId = performedSet.subExerciseId
            // Counter initialisieren
            if (subExerciseSetCounters[subId] === undefined) subExerciseSetCounters[subId] = 0

            const currentSetIndexForSub = subExerciseSetCounters[subId]

            // Die entsprechende Unter-Übung im Plan-Kopie finden
            const subExerciseInCopy = exerciseToUpdateInCopy.exercises.find((e) => e._id === subId)

            // Prüfen, ob im Plan ein Satz an dieser Stelle existiert
            if (subExerciseInCopy && subExerciseInCopy.sets && subExerciseInCopy.sets[currentSetIndexForSub]) {
              // Neue Werte holen
              const newWeightSuggestion = performedSet.find((item) => item.text === "newWeight")?.value
              const actualLiftedWeight = performedSet.find((item) => item.text === "Gewicht")?.value

              let finalWeightToSet = null
              if (newWeightSuggestion != null) finalWeightToSet = newWeightSuggestion
              else if (actualLiftedWeight != null) finalWeightToSet = actualLiftedWeight

              // Im Plan speichern
              if (finalWeightToSet !== null) {
                const weightSetting = subExerciseInCopy.sets[currentSetIndexForSub].find((s) => s.text === "Gewicht")
                if (weightSetting) {
                  weightSetting.value = finalWeightToSet
                }
              }
            }

            // Counter hochzählen (Skipped Sätze zählen im Plan-Index meist trotzdem mit,
            // es sei denn du willst übersprungene Sätze nicht als "erledigt" im Plan werten)
            if (!performedSet.skipped) {
              subExerciseSetCounters[subId]++
            }
          })

          // Supersatz ist hiermit erledigt, weiter zur nächsten Übung
          return
        }

        // === FALL B: NORMALE ÜBUNG ===
        const originalExercise = this.originalActiveSplit.exercises.find((ex) => ex._id === performedExerciseId)

        // Sicherheitscheck: Wenn normale Übung keine Sets hat, abbrechen
        if (!exerciseToUpdateInCopy.sets || !originalExercise || !originalExercise.sets) return

        const hasWarmup = originalExercise.warmup && originalExercise.warmup.length > 0

        performedSets.forEach((performedSet, performedSetIndex) => {
          if (hasWarmup && performedSetIndex === 0) return
          const regularSetIndex = hasWarmup ? performedSetIndex - 1 : performedSetIndex

          const newWeightSuggestion = performedSet.find((item) => item.text === "newWeight")?.value
          const actualLiftedWeight = performedSet.find((item) => item.text === "Gewicht")?.value

          let finalWeightToSet = null
          if (newWeightSuggestion != null) finalWeightToSet = newWeightSuggestion
          else if (actualLiftedWeight != null) finalWeightToSet = actualLiftedWeight

          if (finalWeightToSet !== null) {
            const setToUpdateInPlan = exerciseToUpdateInCopy.sets[regularSetIndex]
            if (setToUpdateInPlan) {
              const weightSettingInPlan = setToUpdateInPlan.find((s) => s.text === "Gewicht")
              if (weightSettingInPlan) weightSettingInPlan.value = finalWeightToSet
            }
          }
        })
      })

      // 3. Notizen aktualisieren
      Object.entries(this.exerciseNotesMap).forEach(([exerciseIndex, note]) => {
        const exerciseInActiveSplit = this.activeSplit.exercises[exerciseIndex]
        if (exerciseInActiveSplit) {
          const exerciseToUpdateInCopy = splitCopy.exercises.find((ex) => ex._id === exerciseInActiveSplit._id)
          if (exerciseToUpdateInCopy) this.$set(exerciseToUpdateInCopy, "userNote", note)
        }
      })

      return splitCopy
    },
    didWeightsChange() {
      if (this.getWorkoutModifications.length > 0) return true

      if (Object.keys(this.completedSetsMap).length > 0) return true

      return false
    },
    handleSaveChanges(mods) {
      this.saveChangesDialogVisible = false
      this.updatePlanInBackend(mods)
    },
    handleDiscardChanges() {
      this.saveChangesDialogVisible = false
    },
    async updatePlanInBackend(mods) {
      const updatedSplit = this.getUpdatedSplit(mods)
      await api.workoutApi.updateSplit({ workout_id: this.activePlanID, split: updatedSplit })
    },

    // Animation
    animateStats() {
      this.manualCalories = Math.floor(this.calculatedCalories)
      this.animateValue("animatedVolume", this.totalVolume, 1500)
      this.animateValue("animatedSets", this.totalSets, 1000)
      this.animateValue("animatedReps", this.totalReps, 1200)
      this.animateValue("animatedDistance", this.totalDistance, 1200)
      this.animateValue("animatedCalories", this.calculatedCalories, 1400)
    },
    animateValue(key, end, duration) {
      let start = null
      const startVal = 0
      const step = (ts) => {
        if (!start) start = ts
        const prog = Math.min((ts - start) / duration, 1)
        const ease = 1 - Math.pow(1 - prog, 3)
        this[key] = Math.floor(ease * (end - startVal) + startVal)
        if (prog < 1) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    },
    triggerConfetti() {
      confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 }, colors: [this.splitColor, "#fff"] })
    },
  },
  mounted() {
    this.animateStats()
    this.setInitialFunFact()
    setTimeout(this.triggerConfetti, 500)

    if (this.getWorkoutModifications.length > 0 && !this.activeSplit.isFreeTraining) {
      setTimeout(() => {
        this.saveChangesDialogVisible = true
      }, 2000) // 2 Sekunden Verzögerung, damit man erst das Design geniessen kann
    }
  },
}
</script>

<style scoped>
/* --- MAIN LAYOUT & BG --- */
.summary-page {
  width: 100%;
  min-height: 100vh;
  background-color: #000;
  color: white;
  font-family: "Roboto", sans-serif;
  overflow-x: hidden;
  position: relative;
}

.fixed-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, #111 0%, #000 100%); /* Aufgehellter Background */
  z-index: 0;
  pointer-events: none;
}
.ambient-light {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  opacity: 0.35;
}
.main-spot {
  top: -20%;
  left: -20%;
  width: 100%;
  height: 60vh;
}
.secondary-spot {
  bottom: -10%;
  right: -20%;
  width: 80%;
  height: 40vh;
  opacity: 0.15;
}
.noise-texture {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
  z-index: 1;
}

/* --- HEADER --- */
.glass-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  z-index: 100;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.header-content {
  text-align: center;
  line-height: 1.1;
}
.header-title {
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 1px;
}
.header-date {
  display: block;
  font-size: 0.7rem;
  color: #aaa;
}
.scroll-content {
  position: relative;
  z-index: 2;
  padding-top: 60px;
}

/* --- COMPONENTS --- */
.hero-avatar {
  border-radius: 50%;
  width: 120px;
  height: 120px;
  margin: 0 auto;
  border: 4px solid;
  padding: 4px;
  background: #111;
  overflow: hidden;
}
.shadow-text {
  text-shadow: 0 10px 30px rgba(0, 0, 0, 0.8);
}

/* Glass Cards */
.glass-card {
  background: rgba(255, 255, 255, 0.07); /* Heller, besserer Kontrast */
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  transition: transform 0.2s;
}
.full-width {
  grid-column: span 2;
}
.relative {
  position: relative;
}
.stat-hero {
  grid-column: span 2;
  min-height: 140px;
}
.bg-icon {
  position: absolute;
  right: -20px;
  bottom: -30px;
  opacity: 0.08;
  transform: rotate(-15deg);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* Edit Circle */
.edit-circle {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
}
.ripple-effect:active {
  transform: scale(0.98);
}

/* Textareas (Fixed visibility) */
.bright-input >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.12) !important;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.bright-input >>> input::placeholder,
.bright-input >>> textarea::placeholder {
  color: #ccc !important;
  opacity: 1;
}
.bright-input >>> textarea {
  color: white !important;
}

/* Footer */
.transparent-footer {
  background: transparent !important;
  z-index: 90;
  bottom: 0;
}
.footer-blur {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.95) 40%, transparent);
  pointer-events: none;
  z-index: -1;
}
.save-btn {
  letter-spacing: 1px;
}

/* Dialogs */
.dialog-card {
  box-shadow: none !important;
  overflow: visible !important;
}
.glass-modal {
  background: #1e1e1e !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px !important;
}
.input-display {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 16px;
  padding: 12px;
  display: flex;
  justify-content: center;
  align-items: baseline;
}
.big-input {
  background: transparent;
  border: none;
  outline: none;
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  text-align: right;
  width: 100px;
}
.unit {
  color: #888;
  font-weight: bold;
  margin-left: 8px;
}
.action-card {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  cursor: pointer;
}

/* PR Slider */
.pr-card {
  min-width: 170px;
  background: linear-gradient(145deg, #222, #111);
}
.section-title {
  font-size: 0.8rem;
  font-weight: 900;
  color: #888;
  letter-spacing: 1px;
}

/* Hyrox */
.hyrox-title {
  font-family: "Arial Black", sans-serif;
  font-size: 2.5rem;
  line-height: 1;
  margin-bottom: 8px;
}
.hyrox-time {
  font-size: 3rem;
  font-weight: bold;
  color: white;
  line-height: 1;
  margin-bottom: 8px;
}
.glass-list-item {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
}
.history-bg {
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.glass-card {
  background: rgba(30, 30, 30, 0.6) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 20px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.glow-bg {
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.03) 0%, transparent 70%);
  transform: rotate(-15deg);
  pointer-events: none;
}

.fun-fact-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid; /* Farbe kommt inline */
  display: flex;
  align-items: center;
  justify-content: center;
}

.fun-fact-icon {
  filter: drop-shadow(0 0 10px currentColor);
}

@keyframes float-icon {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.tap-hint-badge {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 4px 8px;

  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 1px;
  color: white;

  display: flex;
  align-items: center;

  /* Die Puls-Animation */
  animation: gentle-pulse 2s infinite ease-in-out;
  pointer-events: none; /* Klick geht durch auf die Karte */
}

@keyframes gentle-pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.1);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0);
    background: rgba(255, 255, 255, 0.2);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
    background: rgba(255, 255, 255, 0.1);
  }
}
</style>
